import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Table,
  Button,
  Input,
  Space,
  Tag,
  Popconfirm,
  message,
  Modal,
  Form,
  DatePicker,
  Typography,
  Avatar,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  QrcodeOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  StarOutlined,
  StarFilled,
  UserOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';
import dayjs from 'dayjs';
import PagesWrapper from '../../../PagesWrapper';
import * as eventsService from '../../../services/eventsService';
import { getFileUrl } from '../../../utils/urlUtils';
import { useAuth } from '../../../context/AuthContext';
import { AppRoles } from '../../../services/authService';
import styles from './EventsList.module.css';

const { Search } = Input;
const { Title, Text } = Typography;

const EventsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { hasRole } = useAuth();
  const isAdmin = hasRole(AppRoles.Admin);

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [qrEvent, setQrEvent] = useState(null);

  const fetchEvents = useCallback(async (page = 1, search = searchText) => {
    setLoading(true);
    try {
      const response = await eventsService.getEvents({
        page,
        pageSize: pagination.pageSize,
        search: search || undefined,
      });
      setEvents(response.items || []);
      setPagination(prev => ({
        ...prev,
        current: response.page,
        total: response.totalCount,
      }));
    } catch (error) {
      message.error(t('events.fetchError', 'Failed to load events'));
    } finally {
      setLoading(false);
    }
  }, [pagination.pageSize, searchText, t]);

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleSearch = (value) => {
    setSearchText(value);
    fetchEvents(1, value);
  };

  const handleRefresh = () => {
    fetchEvents(pagination.current, searchText);
  };

  const handleTableChange = (paginationConfig) => {
    fetchEvents(paginationConfig.current);
  };

  const handleCreate = () => {
    navigate('/admin/events/new');
  };

  const handleEdit = (event) => {
    navigate(`/admin/events/${event.id}`);
  };

  const handleDelete = async (event) => {
    try {
      await eventsService.deleteEvent(event.id);
      message.success(t('events.deleteSuccess', 'Event deleted successfully'));
      fetchEvents(pagination.current);
    } catch (error) {
      message.error(t('events.deleteError', 'Failed to delete event'));
    }
  };

  const handleTogglePublish = async (event) => {
    try {
      await eventsService.updateEvent(event.id, { isPublished: !event.isPublished });
      message.success(event.isPublished 
        ? t('events.unpublishSuccess', 'Event unpublished') 
        : t('events.publishSuccess', 'Event published'));
      fetchEvents(pagination.current);
    } catch (error) {
      message.error(t('events.togglePublishError', 'Failed to update event status'));
    }
  };

  const handleToggleFeatured = async (event) => {
    try {
      await eventsService.updateEvent(event.id, { isFeatured: !event.isFeatured });
      message.success(event.isFeatured 
        ? t('events.unfeaturedSuccess', 'Event removed from featured') 
        : t('events.featuredSuccess', 'Event marked as featured'));
      fetchEvents(pagination.current);
    } catch (error) {
      message.error(t('events.toggleFeaturedError', 'Failed to update featured status'));
    }
  };

  const handleCopyLink = (event) => {
    const url = `${window.location.origin}/events/${event.id}`;
    navigator.clipboard.writeText(url);
    message.success(t('events.linkCopied', 'Event link copied to clipboard'));
  };

  const handleOpenEvent = (event) => {
    window.open(`/events/${event.id}`, '_blank');
  };

  const handleShowQrCode = (event) => {
    setQrEvent(event);
    setQrModalVisible(true);
  };

  const handleDownloadQrCode = () => {
    const svg = document.getElementById('event-qr-code');
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      
      const pngUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `${qrEvent?.title || 'event'}-qrcode.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return dayjs(dateString).format('DD/MM/YYYY HH:mm');
  };

  const isEventPast = (endDateTime) => {
    return dayjs(endDateTime).isBefore(dayjs());
  };

  const columns = [
    {
      title: t('events.title', 'Title'),
      dataIndex: 'title',
      key: 'title',
      width: 350,
      render: (text, record) => (
        <div className={styles.eventTitle} onClick={() => handleEdit(record)}>
          {record.bannerImageUrl && (
            <Avatar 
              shape="square" 
              size={40} 
              src={getFileUrl(record.bannerImageUrl)} 
              className={styles.eventThumb}
            />
          )}
          <span className={isEventPast(record.endDateTime) ? styles.pastEvent : ''}>
            {text}
          </span>
        </div>
      ),
    },
    {
      title: t('events.dateTime', 'Date & Time'),
      key: 'dateTime',
      width: 180,
      render: (_, record) => (
        <div className={styles.dateTime}>
          <div>
            <CalendarOutlined className={styles.icon} />
            {formatDateTime(record.startDateTime)}
          </div>
          <Text type="secondary" className={styles.toDate}>
            → {formatDateTime(record.endDateTime)}
          </Text>
        </div>
      ),
    },
    {
      title: t('events.location', 'Location'),
      dataIndex: 'location',
      key: 'location',
      width: 300,
      ellipsis: true,
      render: (text) => (
        <span>
          <EnvironmentOutlined className={styles.icon} />
          {text}
        </span>
      ),
    },
    {
      title: t('events.columns.registrations', 'Registrations'),
      dataIndex: 'registrationCount',
      key: 'registrationCount',
      width: 110,
      render: (count) => (
        <span>
          <TeamOutlined className={styles.icon} />
          {count}
        </span>
      ),
    },
    {
      title: t('events.status', 'Status'),
      key: 'status',
      width: 140,
      render: (_, record) => (
        <Space direction="vertical" size={3}>
          <Tag
            color={record.isPublished ? 'green' : 'default'}
            icon={record.isPublished ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
          >
            {record.isPublished ? t('events.published', 'Published') : t('events.draft', 'Draft')}
          </Tag>
          {record.isFeatured && (
            <Tag color="gold" icon={<StarFilled />}>
              {t('events.featured', 'Featured')}
            </Tag>
          )}
          {isEventPast(record.endDateTime) && (
            <Tag color="red">{t('events.ended', 'Ended')}</Tag>
          )}
        </Space>
      ),
    },
    {
      title: t('events.createdBy', 'Created By'),
      key: 'addedBy',
      width: 250,
      render: (_, record) => (
        <Space size="small">
          <Avatar 
            size="small" 
            src={getFileUrl(record.addedByProfileImageUrl)}
            icon={!record.addedByProfileImageUrl && <UserOutlined />}
          />
          <span>{record.addedByName || t('common.unknown', 'Unknown')}</span>
        </Space>
      ),
    },
    {
      title: t('events.actions', 'Actions'),
      key: 'actions',
      width: 280,
      render: (_, record) => (
        <Space size="small" className={styles.actions}>
          <Tooltip title={t('events.edit', 'Edit')}>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title={record.isPublished ? t('events.unpublish', 'Unpublish') : t('events.publish', 'Publish')}>
            <Button
              type="text"
              icon={record.isPublished ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
              onClick={() => handleTogglePublish(record)}
            />
          </Tooltip>
          {isAdmin && (
            <Tooltip title={record.isFeatured ? t('events.unfeatured', 'Remove Featured') : t('events.makeFeatured', 'Make Featured')}>
              <Button
                type="text"
                icon={record.isFeatured ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />}
                onClick={() => handleToggleFeatured(record)}
              />
            </Tooltip>
          )}
          {record.isPublished && (
            <Tooltip title={t('events.copyLink', 'Copy Link')}>
              <Button
                type="text"
                icon={<CopyOutlined />}
                onClick={() => handleCopyLink(record)}
              />
            </Tooltip>
          )}
          {record.isPublished && (
            <Tooltip title={t('events.qrCode', 'QR Code')}>
              <Button
                type="text"
                icon={<QrcodeOutlined />}
                onClick={() => handleShowQrCode(record)}
              />
            </Tooltip>
          )}
          {record.isPublished && (
            <Tooltip title={t('events.openEvent', 'Open Event Page')}>
              <Button
                type="text"
                icon={<PlayCircleOutlined />}
                onClick={() => handleOpenEvent(record)}
              />
            </Tooltip>
          )}
          <Popconfirm
            title={t('events.deleteConfirm', 'Are you sure you want to delete this event?')}
            onConfirm={() => handleDelete(record)}
            okText={t('common.yes', 'Yes')}
            cancelText={t('common.no', 'No')}
          >
            <Tooltip title={t('events.delete', 'Delete')}>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <PagesWrapper>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.titleSection}>
            <CalendarOutlined className={styles.titleIcon} />
            <Title level={2} className={styles.title}>
              {t('events.title', 'Events')}
            </Title>
          </div>
          <Text type="secondary">
            {t('events.description', 'Create and manage events for your organization')}
          </Text>
        </div>

        <div className={styles.toolbar}>
          <Space>
            <Search
              placeholder={t('events.searchPlaceholder', 'Search events...')}
              allowClear
              onSearch={handleSearch}
              onChange={(e) => !e.target.value && handleSearch('')}
              style={{ width: 300 }}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
            >
              {t('common.refresh', 'Refresh')}
            </Button>
          </Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            {t('events.createEvent', 'Create Event')}
          </Button>
        </div>

        <Table
          dataSource={events}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: false,
            showTotal: (total) => t('common.totalItems', 'Total: {{count}} items', { count: total }),
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />

        {/* QR Code Modal */}
        <Modal
          title={t('events.qrCodeTitle', 'Event QR Code')}
          open={qrModalVisible}
          onCancel={() => setQrModalVisible(false)}
          footer={[
            <Button key="download" type="primary" onClick={handleDownloadQrCode}>
              {t('events.downloadQr', 'Download QR Code')}
            </Button>,
            <Button key="close" onClick={() => setQrModalVisible(false)}>
              {t('common.close', 'Close')}
            </Button>,
          ]}
          centered
        >
          {qrEvent && (
            <div className={styles.qrContainer}>
              <QRCodeSVG
                id="event-qr-code"
                value={`${window.location.origin}/events/${qrEvent.id}`}
                size={256}
                level="H"
                includeMargin
              />
              <Text className={styles.qrEventName}>{qrEvent.title}</Text>
              <Text type="secondary" className={styles.qrUrl}>
                {window.location.origin}/events/{qrEvent.id}
              </Text>
            </div>
          )}
        </Modal>
      </div>
    </PagesWrapper>
  );
};

export default EventsList;
